import { ApolloLink } from "@apollo/client";

// Import generated DateTime fields mapping (only available after schema generation)
let DATE_TIME_FIELDS_MAP: Record<string, string[]> = {};

try {
  // Dynamically import the generated file to handle cases where it might not exist yet
  const {
    DATE_TIME_FIELDS_MAP: generatedMap
  } = require("../../generated/dateTimeFields");
  DATE_TIME_FIELDS_MAP = generatedMap;
} catch (error) {
  if (process.env.NODE_ENV === "development") {
    console.warn(
      '⚠️  DateTime fields map not found. Run "npm run graphql:generate-datetime-fields" to generate it.'
    );
    console.warn("   Falling back to heuristic DateTime detection.");
  }
}

/**
 * Apollo Link that safely transforms DateTime scalars bidirectionally:
 * - Outgoing: Converts Date objects to ISO strings for GraphQL
 * - Incoming: Converts ISO date strings to JavaScript Date objects (only for known DateTime fields)
 */
export const dateTimeTransformLink = new ApolloLink((operation, forward) => {
  // Transform outgoing variables (Date objects → ISO strings)
  if (operation.variables) {
    operation.variables = transformDateObjectsToStrings(
      operation.variables
    ) as Record<string, unknown>;
  }

  // Transform incoming response data (ISO strings → Date objects)
  return forward(operation).map(response => {
    if (response.data) {
      response.data = transformDateTimeFields(
        response.data
      ) as typeof response.data;
    }
    return response;
  });
});

/**
 * Recursively transforms DateTime strings to Date objects
 * Only transforms fields that are known to be DateTime scalars from the schema
 */
function transformDateTimeFields(obj: unknown, typeName?: string): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => transformDateTimeFields(item, typeName));
  }

  if (typeof obj === "object") {
    const transformed: Record<string, unknown> = {};

    // Get the GraphQL type name (__typename is automatically added by Apollo)
    const objectTypeName =
      ((obj as Record<string, unknown>).__typename as string | undefined) ||
      typeName;
    const knownDateTimeFields = objectTypeName
      ? DATE_TIME_FIELDS_MAP[objectTypeName]
      : undefined;

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = (obj as Record<string, unknown>)[key];

        if (knownDateTimeFields && knownDateTimeFields.includes(key)) {
          // This field is confirmed to be a DateTime scalar - safe to transform
          if (typeof value === "string" && isISODateString(value)) {
            transformed[key] = new Date(value);
          } else {
            transformed[key] = value;
          }
        } else if (
          !knownDateTimeFields &&
          typeof value === "string" &&
          isISODateString(value)
        ) {
          // Fallback: no type information available, use heuristic (development mode warning)
          if (process.env.NODE_ENV === "development") {
            console.warn(
              `🤔 Heuristic DateTime detection for field "${key}" in type "${
                objectTypeName || "unknown"
              }". Consider running schema generation.`
            );
          }
          transformed[key] = new Date(value);
        } else {
          // Recursively transform nested objects
          transformed[key] = transformDateTimeFields(value, objectTypeName);
        }
      }
    }
    return transformed;
  }

  return obj;
}

/**
 * Checks if a string is a valid ISO 8601 DateTime string
 * More restrictive to avoid false positives
 */
function isISODateString(value: string): boolean {
  // .NET DateTime scalars can include seven fractional digits (100-nanosecond ticks).
  const isoDateRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,7})?(Z|[+-]\d{2}:?\d{2})$/;

  if (!isoDateRegex.test(value)) {
    return false;
  }

  // Validate it's actually a valid date
  const date = new Date(value);
  return !isNaN(date.getTime());
}

/**
 * Transforms Date objects to ISO strings for GraphQL variables
 * This handles outgoing data (mutations and query variables)
 */
function transformDateObjectsToStrings(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (obj instanceof Date) {
    return obj.toISOString();
  }

  if (Array.isArray(obj)) {
    return obj.map(transformDateObjectsToStrings);
  }

  if (typeof obj === "object") {
    const transformed: Record<string, unknown> = {};
    for (const key in obj as Record<string, unknown>) {
      if ((obj as Record<string, unknown>).hasOwnProperty(key)) {
        transformed[key] = transformDateObjectsToStrings(
          (obj as Record<string, unknown>)[key]
        );
      }
    }
    return transformed;
  }

  return obj;
}
